import React, {
  forwardRef, useState, useRef, useEffect, useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import { FieldLabel } from '@puckeditor/core';
import { Input, Button } from 'reactstrap';

export const getPuckClassNameFactory = (base, styles = {}) => (slot) => {
  if (slot) {
    return styles[`${base}-${slot}`] || `${base}-${slot}`;
  }
  return styles[base] || base;
};

// eslint-disable-next-line no-underscore-dangle
export const resolveBg = (_bg) => {
  if (!_bg || _bg.type === 'none') return undefined;
  if (_bg.type === 'solid') return _bg.color || undefined;
  if (_bg.type === 'gradient') {
    const start = _bg.gradientStart || '#ffffff';
    const end = _bg.gradientEnd || '#000000';
    const deg = _bg.gradientDeg ?? 135;
    return `linear-gradient(${deg}deg, ${start}, ${end})`;
  }
  return undefined;
};

const ColorSwatch = ({ color }) => (
  <div
    className="puck-bg-swatch"
    style={{ background: color || 'transparent' }}
  />
);

ColorSwatch.propTypes = { color: PropTypes.string };
ColorSwatch.defaultProps = { color: '' };

const useClickOutside = (ref, onClose) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
};

const ColorPickerRow = ({ label, color, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(color);
  const wrapRef = useRef(null);
  const closepicker = useCallback(() => setOpen(false), []);
  useClickOutside(wrapRef, closepicker);

  useEffect(() => { setHexInput(color); }, [color]);

  const commitHex = (val) => {
    const hex = val.startsWith('#') ? val : `#${val}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) onChange(hex);
  };

  return (
    <div className="puck-bg-picker-row" ref={wrapRef}>
      {label && <span className="puck-bg-picker-label">{label}</span>}
      <Button
        type="button"
        aria-label="Pick color"
        className="puck-bg-swatch-btn"
        style={{ background: color }}
        onClick={() => setOpen((o) => !o)}
      />
      <Input
        type="text"
        className="puck-bg-hex-input"
        bsSize="sm"
        value={hexInput}
        onChange={(e) => setHexInput(e.target.value)}
        onBlur={(e) => commitHex(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && commitHex(e.target.value)}
        maxLength={7}
        spellCheck={false}
      />
      {open && (
        <div className="puck-bg-picker-popover">
          <ChromePicker
            color={color}
            onChange={(c) => { onChange(c.hex); setHexInput(c.hex); }}
            disableAlpha
          />
        </div>
      )}
    </div>
  );
};

ColorPickerRow.propTypes = {
  label: PropTypes.string,
  color: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

ColorPickerRow.defaultProps = {
  label: null,
};

export const BgField = ({ value, onChange }) => {
  const intl = useIntl();
  const bg = value || {
    type: 'none', color: '#ffffff', gradientStart: '#ffffff', gradientEnd: '#000000', gradientDeg: 135,
  };

  const set = (patch) => onChange({ ...bg, ...patch });

  return (
    <div className="puck-bgfield-sidebar-wrap">
      <FieldLabel label={intl.formatMessage({ defaultMessage: 'Background' })}>
        <Input
          type="select"
          bsSize="sm"
          value={bg.type}
          onChange={(e) => set({ type: e.target.value })}
          className="mb-2 puck-dropdown"
        >
          <option value="none">{intl.formatMessage({ defaultMessage: 'Transparent' })}</option>
          <option value="solid">{intl.formatMessage({ defaultMessage: 'Solid color' })}</option>
          <option value="gradient">{intl.formatMessage({ defaultMessage: 'Gradient' })}</option>
        </Input>

        {bg.type === 'solid' && (
          <ColorPickerRow
            color={bg.color || '#ffffff'}
            onChange={(hex) => set({ color: hex })}
          />
        )}

        {bg.type === 'gradient' && (
          <>
            <ColorPickerRow
              label={intl.formatMessage({ defaultMessage: 'Start' })}
              color={bg.gradientStart || '#ffffff'}
              onChange={(hex) => set({ gradientStart: hex })}
            />
            <ColorPickerRow
              label={intl.formatMessage({ defaultMessage: 'End' })}
              color={bg.gradientEnd || '#000000'}
              onChange={(hex) => set({ gradientEnd: hex })}
            />
            <FieldLabel label={intl.formatMessage({ defaultMessage: 'Angle (deg)' })} className="mt-1 mb-2 pb-0">
              <Input
                type="number"
                bsSize="sm"
                min={0}
                max={360}
                value={bg.gradientDeg ?? 135}
                onChange={(e) => set({ gradientDeg: Number(e.target.value) })}
              />
            </FieldLabel>
            <div
              className="puck-bg-gradient-preview"
              style={{ background: resolveBg(bg) }}
            />
          </>
        )}
      </FieldLabel>
    </div>
  );
};

BgField.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

BgField.defaultProps = {
  value: null,
};

const Layout = forwardRef(
  ({
    children, className, style,
  }, ref) => (
    <div
      className={className}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  ),
);

Layout.displayName = 'Layout';

Layout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

Layout.defaultProps = {
  children: null,
  className: '',
  style: {},
};

export { Layout };

export const layoutFields = {
  // eslint-disable-next-line no-underscore-dangle
  // _bg: {
  //   type: 'custom',
  //   label: 'Background',
  //   render: (props) => <BgField {...props} />,
  // },
};

export const layoutDefaultProps = {
  // eslint-disable-next-line no-underscore-dangle
  _bg: {
    type: 'none',
    color: '#ffffff',
    gradientStart: '#ffffff',
    gradientEnd: '#000000',
    gradientDeg: 135,
  },
};

export function withLayout(componentConfig) {
  return {
    ...componentConfig,
    inline: true,

    fields: {
      ...componentConfig.fields,
      ...layoutFields,
    },

    defaultProps: {
      ...componentConfig.defaultProps,
      ...layoutDefaultProps,
    },

    render: (props) => {
      // eslint-disable-next-line no-underscore-dangle
      const background = resolveBg(props._bg);
      return (
        <Layout
          className="Layout"
          layout={props.layout}
          style={background ? { background } : undefined}
          ref={props.puck?.dragRef}
        >
          {componentConfig.render(props)}
        </Layout>
      );
    },
  };
}
